import { Observable, catchError, forkJoin, map, of, take, tap } from 'rxjs';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import {
  GridRow,
  GridRowsPage,
  GridBucketRowsPage,
  GridBucketScope,
  GridGroupSummaryItem,
  GridSortField
} from '../contracts/public-api';
import type { GridBackendAttributeFilter } from '../d-data-grid/column-filter.types';
import { isStaleQueryGeneration } from './grid-orchestration-query-generation';
import { NULL_BACKEND_SENTINEL, NULL_DISPLAY_KEY } from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import type { GroupPaginationState } from './grid-orchestration.types';
import {
  mergeGroupPaginationIntoElementsData,
  type MergedPaginationElements
} from './grid-orchestration-pagination-state';
import {
  BATCH_BUCKET_SIZE,
  MAX_REQUEST_CONCURRENCY,
  chunkArray,
  forkJoinWithConcurrency,
  normalizeGroupSummaryKey
} from './grid-orchestration.utils';

/** Host callbacks/state accessors used by bucket batch loading helpers. */
export interface BucketLoadingHost {
  readonly elementsProvider: GridOrchestrationDataAccess;
  getQueryGeneration: () => number;
  fetchGridPage: (where: Record<string, unknown>) => Observable<GridRowsPage>;
  buildBucketInputFromPath: (path: string) => GridBucketScope;
  buildWhereFromPath: (path: string) => Record<string, unknown>;
  buildBatchBucketRequestOptions: (buckets: Array<GridBucketScope>) => {
    buckets: Array<GridBucketScope>;
    firstPerBucket: number;
    sharedWhere: Record<string, unknown>;
    attributeFilter?: Array<GridBackendAttributeFilter>;
    sort?: Array<GridSortField>;
  };
  logEmptyNodeMismatch: (bucketKey: string, response: GridRowsPage) => void;
  applyBucketFetchResult: (
    bucketKey: string,
    response: GridRowsPage,
    options?: { clientNullFilter?: boolean; cursorSource?: 'batch' | 'elements' }
  ) => void;
  getGroupPagination: () => Map<string, GroupPaginationState> | null;
  setGroupPagination: (pagination: Map<string, GroupPaginationState>) => void;
}

/**
 * Reports buckets the server counted but returned no rows for, together with the scope that
 * was actually sent.
 *
 * The scope is captured at request time rather than rebuilt from current state, because the
 * failure is intermittent and rebuilding it would show whatever the state has since become
 * rather than what produced the empty page. The sent `where` is the diagnostic that matters:
 * a discriminator that resolved to a display label (`Bridge_Inventory_SNBI_Primary`) instead of
 * its slug (`bridge_inventory_snbi_primary`) matches no rows, which is consistent with the
 * bucket scope being built before the discriminator label map is populated.
 *
 * @param results - Bucket pages returned by `elementsForGroupBuckets`.
 * @param sentScopes - Bucket key → the scope sent for it in this request.
 */
function logEmptyBucketDiagnostics(
  results: ReadonlyArray<GridBucketRowsPage>,
  sentScopes: ReadonlyMap<string, GridBucketScope>
): void {
  for (const result of results) {
    const reportedTotal: number = result.totalCount ?? 0;
    if (reportedTotal <= 0 || result.nodes.length > 0) {
      continue;
    }

    const scope: GridBucketScope | undefined = sentScopes.get(result.bucketKey);
    logGridOrchestration(
      GridOrchestrationLogLevel.Warn,
      `[GridDiag] Bucket '${result.bucketKey}' reported totalCount=${reportedTotal} with 0 nodes.`,
      {
        bucketKey: result.bucketKey,
        reportedTotal,
        sentWhere: scope?.where ?? null,
        sentAttributeFilter: scope?.attributeFilter ?? null,
        scopeCaptured: scope !== undefined
      }
    );
  }
}

/** Loads the first page for each discovered group value via batched bucket requests. */
export function fetchPerGroupFirstPages(
  host: BucketLoadingHost,
  groups: Array<GridGroupSummaryItem>
): Observable<GridRowsPage> {
  const bucketKeys: Array<string> = groups.map((group: GridGroupSummaryItem): string =>
    normalizeGroupSummaryKey(group.value, NULL_BACKEND_SENTINEL, NULL_DISPLAY_KEY)
  );
  return fetchBucketsFirstPages(host, bucketKeys);
}

/** Loads first pages for multiple bucket keys, preferring the batch API. */
export function fetchBucketsFirstPages(
  host: BucketLoadingHost,
  bucketKeys: Array<string>
): Observable<GridRowsPage> {
  if (bucketKeys.length === 0) {
    return of({ nodes: [], totalCount: 0 });
  }

  const queryGeneration: number = host.getQueryGeneration();
  const chunks: Array<Array<string>> = chunkArray(bucketKeys, BATCH_BUCKET_SIZE);

  return forkJoin(
    chunks.map((chunk: Array<string>): Observable<Array<GridBucketRowsPage>> =>
      fetchBucketChunkFirstPages(host, chunk, queryGeneration)
    )
  ).pipe(
    map((chunkResults: Array<Array<GridBucketRowsPage>>): GridRowsPage => {
      if (isStaleQueryGeneration(host, queryGeneration)) {
        return { nodes: [], totalCount: 0 };
      }
      return mergeFirstPageBatchResults(host, chunkResults.flat());
    })
  );
}

/** Fetches first pages for one batch chunk, with per-bucket fallback on error. */
export function fetchBucketChunkFirstPages(
  host: BucketLoadingHost,
  bucketKeys: Array<string>,
  queryGeneration: number = host.getQueryGeneration()
): Observable<Array<GridBucketRowsPage>> {
  const bucketInputs: Array<GridBucketScope> = bucketKeys.map((path: string): GridBucketScope =>
    host.buildBucketInputFromPath(path)
  );
  const sentScopes: ReadonlyMap<string, GridBucketScope> = new Map<string, GridBucketScope>(
    bucketInputs.map((scope: GridBucketScope): [string, GridBucketScope] => [scope.bucketKey, scope])
  );

  return host.elementsProvider
    .getElementsForGroupBuckets(host.buildBatchBucketRequestOptions(bucketInputs))
    .pipe(
      map((results: Array<GridBucketRowsPage>): Array<GridBucketRowsPage> => results),
      take(1),
      tap((results: Array<GridBucketRowsPage>): void => {
        logEmptyBucketDiagnostics(results, sentScopes);
      }),
      catchError((): Observable<Array<GridBucketRowsPage>> => {
        const queries: Array<[string, Observable<GridRowsPage>]> = bucketKeys.map(
          (bucketKey: string): [string, Observable<GridRowsPage>] => [
            bucketKey,
            host.fetchGridPage(host.buildWhereFromPath(bucketKey)).pipe(
              catchError((err: Error): Observable<GridRowsPage> => {
                logGridOrchestration(
                  GridOrchestrationLogLevel.Error,
                  `Failed to load elements for bucket '${bucketKey}':`,
                  err
                );
                return of({ nodes: [], totalCount: 0 });
              }),
              tap((response: GridRowsPage): void => {
                host.logEmptyNodeMismatch(bucketKey, response);
              })
            )
          ]
        );

        return forkJoinWithConcurrency(queries, MAX_REQUEST_CONCURRENCY).pipe(
          map((results: Record<string, GridRowsPage>): Array<GridBucketRowsPage> => {
            if (isStaleQueryGeneration(host, queryGeneration)) {
              return [];
            }
            return Object.entries(results).map(
              ([bucketKey, response]: [string, GridRowsPage]): GridBucketRowsPage => ({
                bucketKey,
                totalCount: response.totalCount ?? response.nodes.length,
                nodes: response.nodes,
                pageInfo: {
                  endCursor: response.pageInfo?.endCursor ?? null,
                  hasNextPage: response.pageInfo?.hasNextPage ?? false
                }
              })
            );
          })
        );
      })
    );
}

/** Applies batch first-page results to pagination and returns the aggregated response. */
export function mergeFirstPageBatchResults(
  host: BucketLoadingHost,
  results: Array<GridBucketRowsPage>
): GridRowsPage {
  const mergeIntoExisting: boolean = host.getGroupPagination() !== null;

  if (mergeIntoExisting) {
    for (const result of results) {
      const response: GridRowsPage = {
        nodes: result.nodes,
        totalCount: result.totalCount,
        pageInfo: result.pageInfo
      };
      host.logEmptyNodeMismatch(result.bucketKey, response);
      host.applyBucketFetchResult(result.bucketKey, response, { cursorSource: 'batch' });
    }
  } else {
    const paginationMap: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>();
    for (const result of results) {
      const response: GridRowsPage = {
        nodes: result.nodes,
        totalCount: result.totalCount,
        pageInfo: result.pageInfo
      };
      host.logEmptyNodeMismatch(result.bucketKey, response);
      paginationMap.set(result.bucketKey, {
        nodes: [...result.nodes],
        endCursor: result.pageInfo?.endCursor ?? null,
        hasNextPage: result.pageInfo?.hasNextPage ?? false,
        totalCount: result.totalCount ?? result.nodes.length,
        loading: false,
        pagesLoaded: 1,
        cursorUsableForElements: false
      });
    }
    host.setGroupPagination(paginationMap);
  }

  const pagination: Map<string, GroupPaginationState> | null = host.getGroupPagination();
  if (pagination !== null) {
    const merged: MergedPaginationElements = mergeGroupPaginationIntoElementsData(pagination);
    return { nodes: merged.nodes, totalCount: merged.totalCount };
  }

  return { nodes: new Array<GridRow>(), totalCount: 0 };
}
